import React, { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { trackPageView, track } from './lib/analytics'
import { ErrorBoundary } from './components/ErrorBoundary'

// Track page views by pathname only.
function RouteTracker() {
  const location = useLocation()
  useEffect(() => { trackPageView(location.pathname) }, [location.pathname])
  return null
}

import { useOfflineSync } from './hooks/useOfflineSync'
import { useOfflineReadiness } from './hooks/useOfflineReadiness'
import { useAnonymousBootstrap } from './hooks/useAnonymousBootstrap'
import { shouldBootstrapAnonymous } from './lib/anonymousBootstrapScope'
import { hasClaimedAnonymousContinuationAllowed } from './lib/claimedAnonymousContinuation'

// Eager routes are part of the common student path.
import { LoginPage } from './pages/LoginPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { SubjectSelectPage } from './pages/SubjectSelectPage'
import { OnboardingPage } from './pages/OnboardingPage'
import { UniversitiesPage } from './pages/UniversitiesPage'
import { CoursesPage } from './pages/CoursesPage'

// Lazy routes are uncommon enough that bundle savings matter more.
const AdminDashboardPage   = lazy(() => import('./pages/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })))
const AdminQuestionsPage   = lazy(() => import('./pages/AdminQuestionsPage').then(m => ({ default: m.AdminQuestionsPage })))
const TeacherDashboardPage = lazy(() => import('./pages/TeacherDashboardPage').then(m => ({ default: m.TeacherDashboardPage })))
const TeacherActivatePage  = lazy(() => import('./pages/TeacherActivatePage').then(m => ({ default: m.TeacherActivatePage })))
const OrganizationDashboardPage = lazy(() => import('./pages/OrganizationDashboardPage').then(m => ({ default: m.OrganizationDashboardPage })))
const OrganizationActivatePage = lazy(() => import('./pages/OrganizationActivatePage').then(m => ({ default: m.OrganizationActivatePage })))
const ParentDashboardPage  = lazy(() => import('./pages/ParentDashboardPage').then(m => ({ default: m.ParentDashboardPage })))
const PricingPage          = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })))
const DemoCheckoutPage     = lazy(() => import('./pages/DemoCheckoutPage').then(m => ({ default: m.DemoCheckoutPage })))
const QuestionPage         = lazy(() => import('./pages/QuestionPage').then(m => ({ default: m.QuestionPage })))
const ResultPage           = lazy(() => import('./pages/ResultPage').then(m => ({ default: m.ResultPage })))
const ProfilePage          = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const PlacementPage        = lazy(() => import('./pages/PlacementPage').then(m => ({ default: m.PlacementPage })))
const DiscoveryPage        = lazy(() => import('./pages/DiscoveryPage').then(m => ({ default: m.DiscoveryPage })))
const RecommendationsPage  = lazy(() => import('./pages/RecommendationsPage').then(m => ({ default: m.RecommendationsPage })))
const RankingPage          = lazy(() => import('./pages/RankingPage').then(m => ({ default: m.RankingPage })))
const UniversityRecommendationsPage = lazy(() => import('./pages/UniversityRecommendationsPage').then(m => ({ default: m.UniversityRecommendationsPage })))
const DiscoverySubjectPicker = lazy(() => import('./pages/DiscoverySubjectPicker').then(m => ({ default: m.DiscoverySubjectPicker })))
const ExamPage            = lazy(() => import('./pages/ExamPage').then(m => ({ default: m.ExamPage })))
const PastExamsPage       = lazy(() => import('./pages/PastExamsPage').then(m => ({ default: m.PastExamsPage })))
const ExamReviewPage      = lazy(() => import('./pages/ExamReviewPage').then(m => ({ default: m.ExamReviewPage })))
const StudentTabBar       = lazy(() => import('./components/StudentTabBar').then(m => ({ default: m.StudentTabBar })))
const PWAInstallBanner    = lazy(() => import('./components/PWAInstallBanner').then(m => ({ default: m.PWAInstallBanner })))
const PostInstallToast    = lazy(() => import('./components/PostInstallToast').then(m => ({ default: m.PostInstallToast })))
const PWAUpdatePrompt     = lazy(() => import('./components/PWAUpdatePrompt').then(m => ({ default: m.PWAUpdatePrompt })))
const ArchiveNoticeBanner = lazy(() => import('./components/ArchiveNoticeBanner').then(m => ({ default: m.ArchiveNoticeBanner })))
const CourseUpdateNoticeBanner = lazy(() => import('./components/CourseUpdateNoticeBanner').then(m => ({ default: m.CourseUpdateNoticeBanner })))
const FlagNoticePopup = lazy(() => import('./components/FlagNoticePopup').then(m => ({ default: m.FlagNoticePopup })))
const FeedbackReplyPopup = lazy(() => import('./components/FeedbackReplyPopup').then(m => ({ default: m.FeedbackReplyPopup })))
const RatingPromptModal   = lazy(() => import('./components/RatingPromptModal').then(m => ({ default: m.RatingPromptModal })))

// Boot loader is eager (~2 KB) because it renders BEFORE any auth check —
// lazy-loading it would surface a Suspense flash instead of the welcome.
import { BootWelcome } from './components/BootWelcome'

// Shared so the anonymous bootstrap scope can be smoke-tested in isolation.
function LandingRedirect() {
  const { user, loading } = useAuth()
  if (loading) {
    return <BootWelcome />
  }
  if (!user || user.is_anonymous) {
    return <Navigate to="/onboarding" replace />
  }
  if (user.role === 'org_manager') return <Navigate to="/organization" replace />
  if (user.role === 'teacher') return <Navigate to="/teacher" replace />
  if (user.role === 'admin' || user.is_admin) return <Navigate to="/admin" replace />
  if (!user.has_programme) return <Navigate to="/onboarding" replace />
  return <Navigate to="/subjects" replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, refresh } = useAuth()
  const location = useLocation()
  const bootstrapping = useAnonymousBootstrap(shouldBootstrapAnonymous(location.pathname), refresh)

  if (loading || bootstrapping) {
    return <BootWelcome />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }
  if (user.role === 'org_manager') return <Navigate to="/organization" replace />

  return <>{children}</>
}

// v2 students must commit a programme before using post-onboarding routes.
function RequireProgrammePicked({ children }: { children: React.ReactNode }) {
  const { user, loading, refresh } = useAuth()
  const location = useLocation()
  const bootstrapping = useAnonymousBootstrap(shouldBootstrapAnonymous(location.pathname), refresh)

  if (loading || bootstrapping) {
    return <BootWelcome variant="returning" />
  }
  if (!user) return <Navigate to="/login" replace />

  if (hasClaimedAnonymousContinuationAllowed() && location.pathname === '/subjects') {
    return <>{children}</>
  }

  const sessionContinuationPath =
    location.pathname === '/questions' ||
    location.pathname === '/result'
  if (sessionContinuationPath) {
    return <>{children}</>
  }

  if (user.onboarding_style_v2 === true && !user.has_programme) {
    track('onboarding_guard_redirect', {
      from: location.pathname,
      to:   '/universities',
    })
    return <Navigate to="/universities" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <BootWelcome />
  }

  if (!user) return <Navigate to="/login" replace />
  if (!user.is_admin && user.role !== 'admin') return <Navigate to="/subjects" replace />

  return <>{children}</>
}

function TeacherRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <BootWelcome />
  }

  if (!user) return <Navigate to="/login" replace />
  // Admins can access teacher portal too; students redirected
  if (user.role !== 'teacher' && user.role !== 'admin' && !user.is_admin) {
    return <Navigate to="/subjects" replace />
  }

  return <>{children}</>
}

function OrganizationRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <BootWelcome />
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'org_manager') return <Navigate to="/subjects" replace />
  return <>{children}</>
}

function AppInner() {
  // Flush queued offline attempts whenever device reconnects
  useOfflineSync()
  useOfflineReadiness()
  return null
}

function GlobalChrome() {
  const location = useLocation()

  // Keep /login as a minimal surface. This avoids pulling in the
  // non-essential app chrome before the auth screen has a chance to render.
  if (
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/reset-password') ||
    location.pathname.startsWith('/organization') ||
    location.pathname.startsWith('/centro/activate')
  ) return null

  return (
    <>
      <PWAInstallBanner />
      <PWAUpdatePrompt />
      <ArchiveNoticeBanner />
      <CourseUpdateNoticeBanner />
      <FlagNoticePopup />
      <FeedbackReplyPopup />
      <RatingPromptModal />
      <PostInstallToast />
      <StudentTabBar />
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <RouteTracker />
      <Suspense fallback={<BootWelcome />}>
      <GlobalChrome />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/subjects"
          element={
            <RequireProgrammePicked>
              <SubjectSelectPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/questions"
          element={
            <RequireProgrammePicked>
              <QuestionPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/result"
          element={
            <RequireProgrammePicked>
              <ResultPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />
        <Route
          path="/teacher"
          element={
            <TeacherRoute>
              <TeacherDashboardPage />
            </TeacherRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <OrganizationRoute>
              <OrganizationDashboardPage />
            </OrganizationRoute>
          }
        />
        <Route
          path="/admin/questions"
          element={
            <AdminRoute>
              <AdminQuestionsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/universities"
          element={
            <ProtectedRoute>
              <UniversitiesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:shortName"
          element={
            <ProtectedRoute>
              <CoursesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/placement"
          element={
            <ProtectedRoute>
              <PlacementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discovery"
          element={
            <RequireProgrammePicked>
              <DiscoveryPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/recommendations"
          element={
            <RequireProgrammePicked>
              <RecommendationsPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/ranking"
          element={
            <RequireProgrammePicked>
              <RankingPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/discovery/start"
          element={
            <RequireProgrammePicked>
              <DiscoverySubjectPicker />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/recommendations/uni/:shortName"
          element={
            <RequireProgrammePicked>
              <UniversityRecommendationsPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/exam"
          element={
            <RequireProgrammePicked>
              <ExamPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/exams/history"
          element={
            <RequireProgrammePicked>
              <PastExamsPage />
            </RequireProgrammePicked>
          }
        />
        <Route
          path="/exam-review/:id"
          element={
            <RequireProgrammePicked>
              <ExamReviewPage />
            </RequireProgrammePicked>
          }
        />
        <Route path="/pricing" element={<PricingPage />} />
        {/* Landing for the synthetic checkout URL used when no gateway is
            configured. Purely informational: it cannot settle a payment. */}
        <Route path="/subscription/demo-checkout" element={<DemoCheckoutPage />} />
        {/* Public, token-authenticated — deliberately NOT wrapped in ProtectedRoute.
            The URL token is the authorization. Parents have no Firebase account. */}
        <Route path="/familia/:token" element={<ParentDashboardPage />} />
        {/* Teacher invite activation — public; the ?token query param is
            the authorization. Lands here from the email invite link. */}
        <Route path="/teacher/activate" element={<TeacherActivatePage />} />
        <Route path="/centro/activate" element={<OrganizationActivatePage />} />
        <Route path="/" element={<LandingRedirect />} />
        <Route path="*" element={<LandingRedirect />} />
      </Routes>
      </Suspense>
      {/* Offline sync — mounts once, flushes queued attempts on reconnect */}
      <AppInner />
    </BrowserRouter>
    </ErrorBoundary>
  )
}

export default App
